import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { ExclamationIcon } from '@heroicons/react/solid';
import DatePicker from 'react-datepicker';

import {
  FormInput, FormTextarea, FormQuestion, FormConditionalSection, FormSave,
} from 'components/form';
import { ValidationsErrors, SuccessMessage } from 'components/notifications';
import { PageWrapper, PageHeaderWrapper, PageTitle } from 'components/page';
import TableWrapper from 'components/table/table-wrapper';
import PageLoadingIndicator from 'components/loading-indicator/page-loading';
import { scrollToTop } from 'utils';
import withAPI from 'hocs/with-api';
import RadioGroup from 'components/radio-group';
import YesNoGroup from 'components/radio-group/yes-no-group';
import PhotoUpload from 'components/photo-upload';
import SignaturePad from 'components/signature-pad';
import { getFormattedDateAPI, getFormattedFullDate } from 'components/dateHandle';

import OtherVehicleFields from './form-components/other-vehicle-fields';
import InjuredPersonFields from './form-components/injured-person-fields';

const ACCIDENT_REPORT_ENDPOINT = 'incidents/driver/accident';

const VEHICLE_CONDITION_OPTIONS = [
  { id: 'No Damage', label: 'No Damage' },
  { id: 'Has issues but safe to operate', label: 'Has issues but safe to operate' },
  { id: 'Not safe to operate', label: 'Not safe to operate' },
];

const POLICE_REPORT_OPTIONS = [
  { id: 'Police report was taken', label: 'Police report was taken' },
  { id: 'Police were not dispatched', label: 'Police were not dispatched' },
];

// Maps the internal `values` field key to the label used in getValidationErrors(),
// so editing one field only clears that field's own red highlight.
const FIELD_ERROR_LABELS = {
  vehicle: 'Vehicle',
  whenAccident: 'Time Accident Occurred',
  location: 'Location',
  driverName: 'Driver Name',
  driverPhone: 'Driver Phone Number',
  supervisorName: "Supervisor's Name",
  contactSupervisor: 'Contact Supervisor',
  fedexBadgeNum: 'FedEx Badge Number',
  fedexBadgePhoto: 'FedEx Badge Photo',
  vehicleCondition: 'Vehicle Condition',
  police: 'Police',
  policeReport: 'Police Report',
  citation: 'Citation',
  propertyDamage: 'Property Damage',
  otherVehicleInvolved: 'Other Vehicle Involvement',
  injuriesPresent: 'Injuries',
  accidentDetails: 'Accident Details',
  accidentScenePhotos: 'Accident Scene Photos',
};

const getInitialValues = () => ({
  companyName: '',
  companyAddress: '',
  companyState: '',
  companyEmail: '',
  companyPhone: '',
  vehicle: '',
  operator: '',
  reportDate: new Date(),

  whenAccident: null,
  location: '',
  driverName: '',
  driverPhone: '',

  supervisorName: '',
  contactSupervisor: '',

  fedexBadgeNum: '',
  fedexBadgePhoto: [],
  driversLicensePhoto: [],
  existingDriversLicense: null,
  vehicleCondition: '',

  police: '',
  policeReport: '',
  policeReportPhoto: [],
  policeReportComment: '',
  citation: '',
  citationPhoto: [],
  citationComment: '',

  propertyDamage: '',
  propertyOwnerContact: '',
  propertyDamagePhotos: [],
  propertyDamageComment: '',

  otherVehicleInvolved: '',
  otherVehicles: [{}],
  vehicleAddMore: ['', ''],

  injuriesPresent: '',
  injuries: [{}],
  injuryAddMore: ['', ''],

  accidentDetails: '',
  accidentScenePhotos: [],
  vehiclePhotos: [],
});

const dataURLToFile = (dataUrl, filename) => {
  const [meta, base64] = dataUrl.split(',');
  const mimeMatch = meta.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
};

const appendField = (formData, key, value) => {
  if (value === undefined || value === null || value === '') return;
  formData.append(key, value);
};

const appendFiles = (formData, key, files) => {
  (files || []).forEach((file) => formData.append(`${key}[]`, file));
};

// 1x1 transparent PNG, used so dev-mode dummy uploads render a real image preview.
const DUMMY_IMAGE_BASE64 = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=';

const createDummyFile = (name) => {
  const binary = atob(DUMMY_IMAGE_BASE64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], name, { type: 'image/png' });
};

const getDummyValues = () => ({
    companyName: 'Acme Trucking LLC',
    companyAddress: '123 Maine St',
    companyState: 'OK',
    companyEmail: 'dispatch@acmetrucking.com',
    companyPhone: '(544) 223-2345',
    vehicle: 'Truck 482',
    operator: 'John Doe',
    reportDate: new Date(),

    whenAccident: new Date(),
    location: '4500 Interstate Dr, Macon, GA 31210',
    driverName: 'John Doe',
    driverPhone: '(555) 123-4567',

    supervisorName: 'Jane Smith',
    contactSupervisor: 'Yes',

    fedexBadgeNum: 'FX-10293847',
    fedexBadgePhoto: [createDummyFile('fedex-badge.png')],
    driversLicensePhoto: [createDummyFile('drivers-license.png')],
    vehicleCondition: 'Has issues but safe to operate',

    police: 'Yes',
    policeReport: 'Police report was taken',
    policeReportPhoto: [createDummyFile('police-report.png')],
    policeReportComment: '',
    citation: 'No',
    citationPhoto: [],
    citationComment: '',

    propertyDamage: 'Yes',
    propertyOwnerContact: 'Mike Johnson, (555) 987-6543, 12 Oak St, Macon GA',
    propertyDamagePhotos: [createDummyFile('property-damage-1.png')],
    propertyDamageComment: '',

    otherVehicleInvolved: 'Yes',
    otherVehicles: [{
      contact: 'Sarah Lee, (555) 222-3333, sarah@example.com',
      driversLicensePhoto: [createDummyFile('other-dl-1.png')],
      licensePlatePhoto: [createDummyFile('other-plate-1.png')],
      insurancePhoto: [createDummyFile('other-ins-1.png')],
      damagePhotos: [createDummyFile('other-damage-1.png')],
    }],
    vehicleAddMore: ['No', ''],

    injuriesPresent: 'Yes',
    injuries: [{
      name: 'John Doe',
      description: 'Minor whiplash, no visible bleeding.',
      photo: [createDummyFile('injury-1.png')],
    }],
    injuryAddMore: ['No', ''],

    accidentDetails: 'While making a right turn at the intersection, another vehicle failed to '
      + 'yield and struck the front passenger side of my truck.',
    accidentScenePhotos: [createDummyFile('scene-1.png'), createDummyFile('scene-2.png')],
    vehiclePhotos: [createDummyFile('vehicle-1.png')],
  });

const DUMMY_SIGNATURE_DATA = [[
  { x: 20, y: 80, time: 0, color: '#1f2933' },
  { x: 60, y: 30, time: 10, color: '#1f2933' },
  { x: 100, y: 90, time: 20, color: '#1f2933' },
  { x: 140, y: 40, time: 30, color: '#1f2933' },
  { x: 180, y: 80, time: 40, color: '#1f2933' },
]];

const isDevMode = () => new URLSearchParams(window.location.search).get('dev') === '1';

const getCachedProfileValues = () => {
  try {
    const profile = JSON.parse(localStorage.getItem('evl-profile'));
    const user = profile?.data;
    const driverNo = user?.driver?.driver_no;
    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');

    const values = {};
    if (driverNo) values.fedexBadgeNum = String(driverNo);
    if (fullName) values.driverName = fullName;
    if (user?.cell_phone) values.driverPhone = user.cell_phone;
    return values;
  } catch {
    return {};
  }
};

const getCachedDriverId = () => {
  try {
    const profile = JSON.parse(localStorage.getItem('evl-profile'));
    return profile?.data?.driver?.id || null;
  } catch {
    return null;
  }
};

const AccidentReportForm = ({ httpRequest }) => {
  const [values, setValues] = useState(getInitialValues);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [truckLookupLoading, setTruckLookupLoading] = useState(false);
  const [truckLookupError, setTruckLookupError] = useState('');
  const signatureRef = useRef(null);
  const topRef = useRef(null);

  useEffect(() => {
    if (isDevMode()) {
      setValues(getDummyValues());
      signatureRef.current?.fromData(DUMMY_SIGNATURE_DATA);
      return;
    }

    setValues((prev) => ({ ...prev, ...getCachedProfileValues() }));

    const driverId = getCachedDriverId();
    if (driverId) {
      httpRequest({
        method: 'get',
        url: `drivers/${driverId}`,
      }).then((response) => {
        const existingDriversLicense = response?.data?.data?.doc_licences?.[0] || null;
        if (existingDriversLicense) {
          setValues((prev) => ({ ...prev, existingDriversLicense }));
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFieldError = (label) => {
    setErrors((prev) => {
      if (!Array.isArray(prev?.errors) || !prev.errors.includes(label)) return prev;
      const remaining = prev.errors.filter((item) => item !== label);
      return remaining.length > 0 ? { ...prev, errors: remaining } : {};
    });
  };

  const handleValue = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    clearFieldError(FIELD_ERROR_LABELS[field]);
    setSubmitted(false);
  };

  const clearCompanyFields = () => ({
    companyName: '', companyAddress: '', companyState: '', companyEmail: '', companyPhone: '',
  });

  const onVehicleBlur = async () => {
    const truckNumber = values.vehicle?.trim();

    if (!truckNumber) {
      setValues((prev) => ({ ...prev, ...clearCompanyFields() }));
      return;
    }

    setTruckLookupError('');
    setTruckLookupLoading(true);

    await httpRequest({
      method: 'get',
      url: `trucks/${encodeURIComponent(truckNumber)}/driver`,
    }).then((response) => {
      const company = response?.data?.data?.company || {};
      const companyName = company.name || '';

      setValues((prev) => ({
        ...prev,
        companyName,
        companyAddress: [company.address, company.city, company.zip].filter(Boolean).join(', '),
        companyState: company.state || '',
        companyEmail: company.email || company.contact_email || '',
        companyPhone: company.phone || company.contact_phone || '',
      }));
      if (!companyName) setTruckLookupError('No company found for this truck number.');
    }).catch(() => {
      setValues((prev) => ({ ...prev, ...clearCompanyFields() }));
      setTruckLookupError('Could not find a company for this truck number.');
    });

    setTruckLookupLoading(false);
  };

  const updateOtherVehicle = (index, updated) => {
    setValues((prev) => {
      const otherVehicles = [...prev.otherVehicles];
      otherVehicles[index] = updated;
      return { ...prev, otherVehicles };
    });
  };

  const updateVehicleAddMore = (index, value) => {
    setValues((prev) => {
      const vehicleAddMore = [...prev.vehicleAddMore];
      vehicleAddMore[index] = value;
      return { ...prev, vehicleAddMore };
    });
  };

  const updateInjury = (index, updated) => {
    setValues((prev) => {
      const injuries = [...prev.injuries];
      injuries[index] = updated;
      return { ...prev, injuries };
    });
  };

  const updateInjuryAddMore = (index, value) => {
    setValues((prev) => {
      const injuryAddMore = [...prev.injuryAddMore];
      injuryAddMore[index] = value;
      return { ...prev, injuryAddMore };
    });
  };

  const visibleVehicleCount = useMemo(() => {
    if (values.otherVehicleInvolved !== 'Yes') return 0;
    let count = 1;
    if (values.vehicleAddMore[0] === 'Yes') {
      count = 2;
      if (values.vehicleAddMore[1] === 'Yes') count = 3;
    }
    return count;
  }, [values.otherVehicleInvolved, values.vehicleAddMore]);

  const visibleInjuryCount = useMemo(() => {
    if (values.injuriesPresent !== 'Yes') return 0;
    let count = 1;
    if (values.injuryAddMore[0] === 'Yes') {
      count = 2;
      if (values.injuryAddMore[1] === 'Yes') count = 3;
    }
    return count;
  }, [values.injuriesPresent, values.injuryAddMore]);

  // `errors.errors` is either our own array of missing-field labels (client-side
  // validation) or a Laravel-style { field: [messages] } bag (backend validation) -
  // guard against the latter so field highlighting never crashes on `.includes`.
  const hasRequiredError = (label) => Array.isArray(errors?.errors) && errors.errors.includes(label);

  const getValidationErrors = () => {
    const problems = [];
    if (!values.vehicle?.trim()) problems.push('Vehicle');
    if (!values.whenAccident) problems.push('Time Accident Occurred');
    if (!values.location?.trim()) problems.push('Location');
    if (!values.driverName?.trim()) problems.push('Driver Name');
    if (!values.driverPhone?.trim()) problems.push('Driver Phone Number');
    if (!values.supervisorName?.trim()) problems.push("Supervisor's Name");
    if (!values.contactSupervisor) problems.push('Contact Supervisor');
    if (!values.fedexBadgeNum?.trim()) problems.push('FedEx Badge Number');
    if (!(values.fedexBadgePhoto?.length > 0)) problems.push('FedEx Badge Photo');
    if (!values.vehicleCondition) problems.push('Vehicle Condition');
    if (!values.police) problems.push('Police');
    if (values.police === 'Yes' && !values.policeReport) problems.push('Police Report');
    if (!values.citation) problems.push('Citation');
    if (!values.propertyDamage) problems.push('Property Damage');
    if (!values.otherVehicleInvolved) problems.push('Other Vehicle Involvement');
    if (!values.injuriesPresent) problems.push('Injuries');
    if (!values.accidentDetails?.trim()) problems.push('Accident Details');
    if (!(values.accidentScenePhotos?.length > 0)) problems.push('Accident Scene Photos');
    if (signatureRef.current?.isEmpty()) problems.push("Driver's Signature");
    return problems;
  };

  const buildFormData = () => {
    const formData = new FormData();

    appendField(formData, 'company_name', values.companyName);
    appendField(formData, 'company_address', values.companyAddress);
    appendField(formData, 'company_state', values.companyState);
    appendField(formData, 'company_email', values.companyEmail);
    appendField(formData, 'company_phone', values.companyPhone);

    appendField(formData, 'vehicle', values.vehicle);
    appendField(formData, 'operator', values.operator);
    appendField(formData, 'report_date', getFormattedDateAPI(values.reportDate));
    appendField(formData, 'when_accident', values.whenAccident ? getFormattedFullDate(values.whenAccident) : '');
    appendField(formData, 'location', values.location);
    appendField(formData, 'driver_name', values.driverName);
    appendField(formData, 'driver_phone', values.driverPhone);

    appendField(formData, 'supervisor_name', values.supervisorName);
    appendField(formData, 'contact_supervisor', values.contactSupervisor);

    appendField(formData, 'fedex_badge_num', values.fedexBadgeNum);
    appendFiles(formData, 'fedex_badge_photo', values.fedexBadgePhoto);
    appendFiles(formData, 'drivers_license_photo', values.driversLicensePhoto);
    appendField(formData, 'vehicle_condition', values.vehicleCondition);

    appendField(formData, 'police', values.police);
    appendField(formData, 'police_report', values.police === 'Yes' ? values.policeReport : '');
    appendFiles(formData, 'police_report_photo', values.policeReportPhoto);
    appendField(formData, 'police_report_comment', values.policeReportComment);
    appendField(formData, 'citation', values.citation);
    appendFiles(formData, 'citation_photo', values.citationPhoto);
    appendField(formData, 'citation_comment', values.citationComment);

    appendField(formData, 'property_damage', values.propertyDamage);
    appendField(formData, 'property_owner_contact', values.propertyDamage === 'Yes' ? values.propertyOwnerContact : '');
    appendFiles(formData, 'property_damage_photos', values.propertyDamagePhotos);
    appendField(formData, 'property_damage_comment', values.propertyDamageComment);

    appendField(formData, 'other_vehicle_involved', values.otherVehicleInvolved);
    values.otherVehicles.slice(0, visibleVehicleCount).forEach((vehicle, index) => {
      const prefix = `other_vehicles[${index}]`;
      appendField(formData, `${prefix}[contact]`, vehicle?.contact);
      appendFiles(formData, `${prefix}[drivers_license_photo]`, vehicle?.driversLicensePhoto);
      appendField(formData, `${prefix}[drivers_license_comment]`, vehicle?.driversLicenseComment);
      appendFiles(formData, `${prefix}[license_plate_photo]`, vehicle?.licensePlatePhoto);
      appendField(formData, `${prefix}[license_plate_comment]`, vehicle?.licensePlateComment);
      appendFiles(formData, `${prefix}[insurance_photo]`, vehicle?.insurancePhoto);
      appendField(formData, `${prefix}[insurance_comment]`, vehicle?.insuranceComment);
      appendFiles(formData, `${prefix}[damage_photos]`, vehicle?.damagePhotos);
    });

    appendField(formData, 'injuries_present', values.injuriesPresent);
    values.injuries.slice(0, visibleInjuryCount).forEach((person, index) => {
      const prefix = `injuries[${index}]`;
      appendField(formData, `${prefix}[name]`, person?.name);
      appendField(formData, `${prefix}[description]`, person?.description);
      appendFiles(formData, `${prefix}[photo]`, person?.photo);
      appendField(formData, `${prefix}[photo_comment]`, person?.photoComment);
    });

    appendField(formData, 'accident_details', values.accidentDetails);
    appendFiles(formData, 'accident_scene_photos', values.accidentScenePhotos);
    appendFiles(formData, 'vehicle_photos', values.vehiclePhotos);

    const signatureDataUrl = signatureRef.current.getTrimmedCanvas().toDataURL('image/png');
    formData.append('signature', dataURLToFile(signatureDataUrl, 'signature.png'));

    return formData;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const problems = getValidationErrors();

    if (problems.length > 0) {
      setSubmitted(false);
      setErrors({ message: 'Please complete these required items:', errors: problems });
      scrollToTop(topRef);
      return;
    }

    setErrors({});
    setIsLoading(true);

    await httpRequest({
      method: 'post',
      url: ACCIDENT_REPORT_ENDPOINT,
      data: buildFormData(),
      timeout: 60000,
    }).then(() => {
      setSubmitted(true);
      setValues(getInitialValues());
      setTruckLookupError('');
      signatureRef.current?.clear();
      scrollToTop(topRef);
    }).catch((error) => {
      setSubmitted(false);
      setErrors(error?.response?.data || { message: 'Request failed. Please try again later.' });
      scrollToTop(topRef);
    });

    setIsLoading(false);
  };

  return (
    <PageWrapper>
      <TableWrapper ref={topRef}>
        <PageLoadingIndicator loading={isLoading} />
        <PageHeaderWrapper>
          <PageTitle title="Accident Report" subtitle="Driver self-report form, filled out at the scene of the accident." />
        </PageHeaderWrapper>

        <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 mb-5 flex items-center gap-2">
          <ExclamationIcon className="h-5 w-5 text-yellow-500 flex-shrink-0" aria-hidden="true" />
          <p className="text-sm font-semibold text-yellow-800">
            Do not leave the scene of the accident until cleared by your manager.
          </p>
        </div>

        {!_.isEmpty(errors) && <ValidationsErrors errors={errors} addClass="mb-5" />}
        {submitted && (
          <SuccessMessage message="Report submitted successfully." addClass="mb-5" />
        )}

        <form onSubmit={onSubmit}>
        <fieldset disabled={isLoading}>
          <div className="bg-white border border-black-100 rounded-md p-5 mb-5">
            <h2 className="text-base font-semibold text-green-regular uppercase tracking-wide border-b border-black-200 pb-2 mb-1">
              Accident Report
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-x-4">
              <FormInput
                label="Vehicle"
                hint="FedEx truck number"
                isRequired
                colspan={3}
                value={values.vehicle}
                placeholder="Truck number"
                handleValue={handleValue}
                onBlur={onVehicleBlur}
                isHighlighted={hasRequiredError('Vehicle')}
                id="vehicle"
              />
              <FormInput label="Operator" colspan={3} value={values.operator} placeholder="Operator" handleValue={handleValue} id="operator" />
              <div className="sm:col-span-3 flex flex-col justify-between py-3">
                <label className="block text-sm font-semibold text-gray-700">Date</label>
                <div className="mt-1">
                  <DatePicker
                    selected={values.reportDate}
                    onChange={(date) => handleValue('reportDate', date)}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500
                      block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-black-100 rounded-md p-5 mb-5">
            <h2 className="text-base font-semibold text-green-regular uppercase tracking-wide border-b border-black-200 pb-2 mb-1">
              Company
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-x-4 gap-y-3">
              <div className="sm:col-span-3">
                <label htmlFor="companyName" className="flex items-center justify-between gap-2">
                  <span className="block text-sm font-medium text-gray-700">Company</span>
                  {!truckLookupLoading && truckLookupError && (
                    <span className="text-xs text-red-regular">{truckLookupError}</span>
                  )}
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="companyName"
                    value={values.companyName}
                    readOnly
                    placeholder="Auto-filled from the truck number"
                    className={`shadow-sm block w-full sm:text-sm rounded-md bg-gray-50
                      text-gray-700 cursor-not-allowed
                      ${!truckLookupLoading && truckLookupError ? 'border-red-300' : 'border-gray-300'}`}
                  />
                </div>
                {truckLookupLoading && (
                  <p className="mt-1 text-xs text-gray-500">Looking up company…</p>
                )}
              </div>

              <FormInput label="State" colspan={3} value={values.companyState} handleValue={handleValue} id="companyState" />
              <FormInput label="Address" colspan={3} value={values.companyAddress} handleValue={handleValue} id="companyAddress" />
              <FormInput label="Email" colspan={3} type="email" value={values.companyEmail} handleValue={handleValue} id="companyEmail" />
              <FormInput label="Phone" colspan={3} type="tel" value={values.companyPhone} handleValue={handleValue} id="companyPhone" />
            </div>
          </div>

          <div className="bg-white border border-black-100 rounded-md p-5 mb-5">
            <h2 className="text-base font-semibold text-green-regular uppercase tracking-wide border-b border-black-200 pb-2 mb-1">
              Inspection Items
            </h2>

            <FormQuestion label="Time Accident Occurred" isRequired hint="Date & Time of Accident">
              <DatePicker
                selected={values.whenAccident}
                onChange={(date) => handleValue('whenAccident', date)}
                showTimeSelect
                dateFormat="MM/dd/yyyy h:mm aa"
                placeholderText="Select date & time"
                className={`shadow-sm focus:ring-indigo-500 block w-full sm:text-sm rounded-md
                  ${hasRequiredError('Time Accident Occurred') ? 'border-red-300 focus:border-red-300' : 'border-gray-300 focus:border-indigo-500'}`}
              />
            </FormQuestion>

            <div className="py-3">
              <FormInput
                label="Location"
                hint="Accident Address"
                isRequired
                colspan={6}
                value={values.location}
                placeholder="Street, City, State, ZIP"
                handleValue={handleValue}
                isHighlighted={hasRequiredError('Location')}
                id="location"
              />
            </div>

            <div className="py-3">
              <FormInput
                label="Driver Name"
                hint="Your Name"
                isRequired
                colspan={6}
                value={values.driverName}
                handleValue={handleValue}
                isHighlighted={hasRequiredError('Driver Name')}
                id="driverName"
              />
            </div>

            <div className="py-3">
              <FormInput
                label="Driver Phone Number"
                hint="Your Phone Number"
                isRequired
                colspan={6}
                type="tel"
                value={values.driverPhone}
                placeholder="(555) 555-5555"
                handleValue={handleValue}
                isHighlighted={hasRequiredError('Driver Phone Number')}
                id="driverPhone"
              />
            </div>
          </div>

          <div className="bg-white border border-black-100 rounded-md p-5 mb-5">
            <h2 className="text-base font-semibold text-green-regular uppercase tracking-wide border-b border-black-200 pb-2 mb-1">
              Supervisor
            </h2>

            <div className="py-3">
              <FormInput
                label="Supervisor's Name"
                hint="Please provide the name of your Supervisor."
                isRequired
                colspan={6}
                value={values.supervisorName}
                handleValue={handleValue}
                isHighlighted={hasRequiredError("Supervisor's Name")}
                id="supervisorName"
              />
            </div>

            <FormQuestion label="Contact Supervisor" isRequired hint="Have you contacted your Supervisor?">
              <YesNoGroup
                value={values.contactSupervisor}
                onChange={(value) => handleValue('contactSupervisor', value)}
                isHighlighted={hasRequiredError('Contact Supervisor')}
              />
            </FormQuestion>
          </div>

          <div className="bg-white border border-black-100 rounded-md p-5 mb-5">
            <h2 className="text-base font-semibold text-green-regular uppercase tracking-wide border-b border-black-200 pb-2 mb-1">
              Driver &amp; Vehicle
            </h2>

            <div className="py-3">
              <FormInput
                label="FedEx Badge Number"
                hint="Provide your FedEx Badge Number."
                isRequired
                colspan={6}
                value={values.fedexBadgeNum}
                handleValue={handleValue}
                isHighlighted={hasRequiredError('FedEx Badge Number')}
                id="fedexBadgeNum"
              />
            </div>

            <div className="py-3">
              <PhotoUpload
                label="FedEx Badge Photo"
                hint="Please provide a photo of your FedEx badge."
                isRequired
                files={values.fedexBadgePhoto}
                onChange={(files) => handleValue('fedexBadgePhoto', files)}
                isHighlighted={hasRequiredError('FedEx Badge Photo')}
              />
            </div>

            <div className="py-3">
              <PhotoUpload
                label="Drivers License"
                hint="Please provide a photo of your Drivers License."
                files={values.driversLicensePhoto}
                onChange={(files) => handleValue('driversLicensePhoto', files)}
                existingFiles={values.existingDriversLicense ? [values.existingDriversLicense] : []}
                onRemoveExisting={() => handleValue('existingDriversLicense', null)}
              />
            </div>

            <FormQuestion label="Vehicle Condition" isRequired hint="Is your vehicle safe to operate?">
              <RadioGroup
                items={VEHICLE_CONDITION_OPTIONS}
                defaultSelected={values.vehicleCondition}
                onChange={(value) => handleValue('vehicleCondition', value)}
                isHighlighted={hasRequiredError('Vehicle Condition')}
              />
            </FormQuestion>
          </div>

          <div className="bg-white border border-black-100 rounded-md p-5 mb-5">
            <h2 className="text-base font-semibold text-green-regular uppercase tracking-wide border-b border-black-200 pb-2 mb-1">
              Police
            </h2>

            <FormQuestion label="Police" isRequired hint="Were police dispatched to the scene?">
              <YesNoGroup
                value={values.police}
                onChange={(value) => handleValue('police', value)}
                isHighlighted={hasRequiredError('Police')}
              />
              <FormConditionalSection show={values.police === 'Yes'}>
                <FormQuestion label="Police Report" isRequired hint="Was there a police report taken? If so, please provide a photo of the Police Report or Receipt.">
                  <RadioGroup
                    items={POLICE_REPORT_OPTIONS}
                    defaultSelected={values.policeReport}
                    onChange={(value) => handleValue('policeReport', value)}
                    isHighlighted={hasRequiredError('Police Report')}
                  />
                  <FormConditionalSection show={values.policeReport === 'Police report was taken'} className="mt-3">
                    <div className="py-3">
                      <PhotoUpload
                        label="Police report photo"
                        hint="or comment on failure"
                        files={values.policeReportPhoto}
                        onChange={(files) => handleValue('policeReportPhoto', files)}
                        allowComment
                        commentValue={values.policeReportComment}
                        onCommentChange={(value) => handleValue('policeReportComment', value)}
                      />
                    </div>
                  </FormConditionalSection>
                </FormQuestion>
              </FormConditionalSection>
            </FormQuestion>

            <FormQuestion label="Citation" isRequired hint="Were you cited? (Did you receive a ticket) If so, please provide a photo of your Citation.">
              <YesNoGroup
                value={values.citation}
                onChange={(value) => handleValue('citation', value)}
                isHighlighted={hasRequiredError('Citation')}
              />
              <FormConditionalSection show={values.citation === 'Yes'}>
                <div className="py-3">
                  <PhotoUpload
                    label="Citation photo"
                    hint="or comment on failure"
                    files={values.citationPhoto}
                    onChange={(files) => handleValue('citationPhoto', files)}
                    allowComment
                    commentValue={values.citationComment}
                    onCommentChange={(value) => handleValue('citationComment', value)}
                  />
                </div>
              </FormConditionalSection>
            </FormQuestion>
          </div>

          <div className="bg-white border border-black-100 rounded-md p-5 mb-5">
            <h2 className="text-base font-semibold text-green-regular uppercase tracking-wide border-b border-black-200 pb-2 mb-1">
              Property Damage
            </h2>

            <FormQuestion label="Property Damage" isRequired hint="Is there damage to property (not another vehicle)? If so, please provide contact information for the property owner.">
              <YesNoGroup
                value={values.propertyDamage}
                onChange={(value) => handleValue('propertyDamage', value)}
                isHighlighted={hasRequiredError('Property Damage')}
              />
              <FormConditionalSection show={values.propertyDamage === 'Yes'}>
                <div className="py-3">
                  <FormTextarea
                    label="Property owner contact information"
                    value={values.propertyOwnerContact}
                    placeholder="Name, phone, address of property owner"
                    handleValue={(value) => handleValue('propertyOwnerContact', value)}
                  />
                </div>
                <div className="py-3">
                  <PhotoUpload
                    label="Property damage photos"
                    hint="up to 4, or comment on failure"
                    max={4}
                    files={values.propertyDamagePhotos}
                    onChange={(files) => handleValue('propertyDamagePhotos', files)}
                    allowComment
                    commentValue={values.propertyDamageComment}
                    onCommentChange={(value) => handleValue('propertyDamageComment', value)}
                  />
                </div>
              </FormConditionalSection>
            </FormQuestion>
          </div>

          <div className="bg-white border border-black-100 rounded-md p-5 mb-5">
            <h2 className="text-base font-semibold text-green-regular uppercase tracking-wide border-b border-black-200 pb-2 mb-1">
              Other Vehicle Involvement
            </h2>

            <FormQuestion label="Did the accident involve another vehicle?" isRequired hint="This drives all of the other-vehicle fields below.">
              <YesNoGroup
                value={values.otherVehicleInvolved}
                onChange={(value) => handleValue('otherVehicleInvolved', value)}
                isHighlighted={hasRequiredError('Other Vehicle Involvement')}
              />
            </FormQuestion>

            <FormConditionalSection show={values.otherVehicleInvolved === 'Yes'}>
              <OtherVehicleFields
                vehicle={values.otherVehicles[0] || {}}
                index={1}
                onChange={(updated) => updateOtherVehicle(0, updated)}
              />

              <FormQuestion label="Was there a second vehicle involved?">
                <YesNoGroup value={values.vehicleAddMore[0]} onChange={(value) => updateVehicleAddMore(0, value)} />
              </FormQuestion>

              <FormConditionalSection show={values.vehicleAddMore[0] === 'Yes'}>
                <OtherVehicleFields
                  vehicle={values.otherVehicles[1] || {}}
                  index={2}
                  onChange={(updated) => updateOtherVehicle(1, updated)}
                />

                <FormQuestion label="Was there a third vehicle involved?">
                  <YesNoGroup value={values.vehicleAddMore[1]} onChange={(value) => updateVehicleAddMore(1, value)} />
                </FormQuestion>

                <FormConditionalSection show={values.vehicleAddMore[1] === 'Yes'}>
                  <OtherVehicleFields
                    vehicle={values.otherVehicles[2] || {}}
                    index={3}
                    onChange={(updated) => updateOtherVehicle(2, updated)}
                  />
                </FormConditionalSection>
              </FormConditionalSection>
            </FormConditionalSection>
          </div>

          <div className="bg-white border border-black-100 rounded-md p-5 mb-5">
            <h2 className="text-base font-semibold text-green-regular uppercase tracking-wide border-b border-black-200 pb-2 mb-1">
              Injuries
            </h2>

            <FormQuestion label="Injuries" isRequired hint="Was anyone injured (including yourself)? If so, please provide a brief description.">
              <YesNoGroup
                value={values.injuriesPresent}
                onChange={(value) => handleValue('injuriesPresent', value)}
                isHighlighted={hasRequiredError('Injuries')}
              />
            </FormQuestion>

            <FormConditionalSection show={values.injuriesPresent === 'Yes'}>
              <InjuredPersonFields
                person={values.injuries[0] || {}}
                index={1}
                onChange={(updated) => updateInjury(0, updated)}
              />

              <FormQuestion label="Was a second person injured?">
                <YesNoGroup value={values.injuryAddMore[0]} onChange={(value) => updateInjuryAddMore(0, value)} />
              </FormQuestion>

              <FormConditionalSection show={values.injuryAddMore[0] === 'Yes'}>
                <InjuredPersonFields
                  person={values.injuries[1] || {}}
                  index={2}
                  onChange={(updated) => updateInjury(1, updated)}
                />

                <FormQuestion label="Was a third person injured?">
                  <YesNoGroup value={values.injuryAddMore[1]} onChange={(value) => updateInjuryAddMore(1, value)} />
                </FormQuestion>

                <FormConditionalSection show={values.injuryAddMore[1] === 'Yes'}>
                  <InjuredPersonFields
                    person={values.injuries[2] || {}}
                    index={3}
                    onChange={(updated) => updateInjury(2, updated)}
                  />
                </FormConditionalSection>
              </FormConditionalSection>
            </FormConditionalSection>
          </div>

          <div className="bg-white border border-black-100 rounded-md p-5 mb-5">
            <h2 className="text-base font-semibold text-green-regular uppercase tracking-wide border-b border-black-200 pb-2 mb-1">
              Accident Details &amp; Photos
            </h2>

            <div className="py-3">
              <FormTextarea
                label="Accident Details"
                isRequired
                hint="Please provide a detailed statement describing your accident. Be as complete as possible."
                value={values.accidentDetails}
                rows={5}
                handleValue={(value) => handleValue('accidentDetails', value)}
                isHighlighted={hasRequiredError('Accident Details')}
              />
            </div>

            <div className="py-3">
              <PhotoUpload
                label="Accident Scene"
                hint="Photos of the accident scene, up to 4 (not a close-up of the damage)."
                isRequired
                max={4}
                files={values.accidentScenePhotos}
                onChange={(files) => handleValue('accidentScenePhotos', files)}
                isHighlighted={hasRequiredError('Accident Scene Photos')}
              />
            </div>

            <div className="py-3">
              <PhotoUpload
                label="Your Vehicle"
                hint="Photos of damage to your vehicle (optional, up to 3)."
                max={3}
                files={values.vehiclePhotos}
                onChange={(files) => handleValue('vehiclePhotos', files)}
              />
            </div>
          </div>

          <div className="bg-white border border-black-100 rounded-md p-5 mb-5">
            <h2 className="text-base font-semibold text-green-regular uppercase tracking-wide border-b border-black-200 pb-2 mb-1">
              Driver&apos;s Signature
            </h2>

            <FormQuestion label="Driver's Signature" isRequired hint="Sign below. Do not leave the scene of the accident until cleared by your manager.">
              <SignaturePad
                ref={signatureRef}
                isHighlighted={hasRequiredError("Driver's Signature")}
                onEnd={() => clearFieldError("Driver's Signature")}
              />
            </FormQuestion>
          </div>

          <div className="flex justify-end pb-5">
            <FormSave label="Submit Report" isLoading={isLoading} isDisabled={isLoading} />
          </div>
        </fieldset>
        </form>
      </TableWrapper>
    </PageWrapper>
  );
};

AccidentReportForm.propTypes = {
  httpRequest: PropTypes.func.isRequired,
};

export default withAPI(AccidentReportForm);
